import { TranslatePipe } from "@ngx-translate/core";
import * as Highcharts from "highcharts";
import { Options } from "highcharts";
import { HighchartsChartModule } from "highcharts-angular";
import HC_more from "highcharts/highcharts-more";

import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";

import { extendHighcharts } from "../../../helpers/highcharts";
import { CertChartOptions } from "./cert-chart-options";
import { CertBubbleLegendGroup, CertBubbleSeries } from "./types";

HC_more(Highcharts);

@Component({
  selector: "cert-chart-generic",
  templateUrl: "cert-chart.html",
  styleUrls: ["cert-chart.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HighchartsChartModule, TranslatePipe],
})
export class CertChartGeneric {
  private platformId = inject(PLATFORM_ID);

  protected series = input.required<CertBubbleSeries[]>();
  protected legendGroups = input.required<CertBubbleLegendGroup[]>();
  protected legendTitle = input.required<string>();
  protected chartTitle = input.required<string>();
  protected loading = input(false);

  // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
  private chartRef = signal<Highcharts.Chart>(null);
  protected highcharts: typeof Highcharts = Highcharts;
  protected chartOptions: Options = CertChartOptions;

  protected isBrowser = computed(() => isPlatformBrowser(this.platformId));

  protected noDataFound = computed(() => !this.loading() && this.haveChart() && this.series().length === 0);
  protected haveChart = computed(() => this.chartRef() && "angular" in this.chartRef());

  constructor() {
    if (this.isBrowser()) {
      extendHighcharts(this.highcharts);
    }

    effect(() => {
      if (
        this.haveChart()
        && this.chartRef().options !== undefined
        && this.legendGroups().length > 0
        && this.series().length > 0
      ) {
        this.setSeries();
      }
    });

    effect(() => {
      if (this.haveChart() && this.chartRef().options !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.loading() ? this.chartRef().showLoading(" ") : this.chartRef().hideLoading();
      }
    });
  }

  setChartInstance(chart: Highcharts.Chart) {
    this.chartRef.set(chart);
  }

  clearSeries() {
    if (!this.haveChart() || this.noDataFound() || !this.chartRef().series) {
      return;
    }

    while (this.chartRef().series.length > 0) {
      this.chartRef().series[0].remove(false);
    }
    this.chartRef().redraw(); // update the visual aspect
  }

  setSeries() {
    if (this.series().length === 0) {
      return;
    }
    this.clearSeries();

    this.series().forEach((serie) => {
      this.chartRef().addSeries(
        {
          name: serie.name,
          type: "packedbubble",
          data: [serie],
        },
        false,
      );
    });

    this.chartRef().redraw(true);
  }
}
